import { Modal, useModals } from 'almanac-ui/Modal'
import { Text } from 'blaze-ui/Text/Text'
import { useState, useCallback } from 'react'
import {
  GoHighLevelIntegration,
  GoHighLevelProfile,
  GoHighLevelBlog,
  GoHighLevelAuthor,
  ExternalIntegrationEnum,
} from 'api/clients/ExternalIntegrationsClient'
import { useUpdateExternalIntegration } from 'hooks/queries/externalIntegrations/useUpdateExternalIntegration'
import { useUpdateBrandPlan } from 'hooks/queries/useUpdateBrandPlan'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { GoHighLevelBlogSelect } from 'components/BlogMetadata/components/integrations/GoHighLevelBlogSelect/GoHighLevelBlogSelect'
import { GoHighLevelAuthorSelect } from 'components/BlogMetadata/components/integrations/GoHighLevelAuthorSelect/GoHighLevelAuthorSelect'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import Styles from '../IntegrationSuccessModal/IntegrationSuccessModal.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'
import { AutopilotInfo } from 'components/IntegrationSuccessModal/AutopilotInfo'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { RoundedPlatformIcon } from 'components/RoundedPlatformIcon/RoundedPlatformIcon'

interface Props {
  integration: GoHighLevelIntegration
  onClose: () => void
  onPressOutside?: () => void
}

export const GoHighLevelConnectedSuccessModal = ({ integration, onClose, onPressOutside }: Props) => {
  const { closeModal } = useModals()
  const { mutateAsync: updateExternalIntegration, isLoading } = useUpdateExternalIntegration()
  const { data: brandPlan } = useCurrentBrandPlan()
  const { mutateAsync: updateBrandPlan } = useUpdateBrandPlan({ id: brandPlan?.id || 0 })
  const workspace = useCurrentWorkspace()

  const profile = integration.additional_data as GoHighLevelProfile
  const availableBlogs: GoHighLevelBlog[] = profile?.blogs || []
  const availableAuthors: GoHighLevelAuthor[] = profile?.authors || []

  const [selectedBlogId, setSelectedBlogId] = useState<string | undefined>(() => {
    if (profile?.selected_blog_id) {
      return profile.selected_blog_id
    }
    if (availableBlogs.length === 1) {
      return availableBlogs[0].id
    }
    return undefined
  })

  const [selectedAuthorId, setSelectedAuthorId] = useState<string | undefined>(() => {
    if (profile?.selected_author_id) {
      return profile.selected_author_id
    }
    if (availableAuthors.length === 1) {
      return availableAuthors[0].id
    }
    return undefined
  })

  const handleClose = useCallback(() => {
    closeModal()
    onClose()
  }, [closeModal, onClose])

  const handleSaveDefaults = useCallback(async () => {
    if (!selectedBlogId || !selectedAuthorId) {
      showToastMessage('Please select both a default blog and author', 'error')
      return
    }

    try {
      // Update the external integration with selected blog and author
      await updateExternalIntegration({
        id: integration.id,
        additional_data: {
          ...profile,
          selected_blog_id: selectedBlogId,
          selected_author_id: selectedAuthorId,
        },
      })

      // Update the brand plan to set Go High Level as the selected blog platform
      if (brandPlan && updateBrandPlan) {
        await updateBrandPlan({
          selected_blog_platform: ExternalIntegrationEnum.GoHighLevel,
        })
      }

      showToastMessage('Saved Successfully', 'success')
      handleClose()
    } catch (error) {
      showToastMessage('Failed to save defaults. Please try again.', 'error')
    }
  }, [
    selectedBlogId,
    selectedAuthorId,
    updateExternalIntegration,
    integration.id,
    profile,
    handleClose,
    brandPlan,
    updateBrandPlan,
  ])

  const canSave = selectedBlogId && selectedAuthorId
  const isAutopilot = workspace?.is_virtual_marketer

  return (
    <Modal.Root
      size="sm"
      blaze
      onPressOutside={onPressOutside}
      onClose={handleClose}
      aria-label="GoHighLevel Account Connected Successfully"
    >
      <Modal.Header title="GoHighLevel account connected!" />
      <Modal.Content>
        <div className={Styles.content}>
          <div className={Styles.titleSection}>
            <div className={Styles.iconContainer}>
              <RoundedPlatformIcon platform={integration.provider} size={24} />
            </div>
            <Heading level={2}>Your GoHighLevel content will start autoposting</Heading>
          </div>

          {isAutopilot && <AutopilotInfo integration={integration} onClose={handleClose} />}

          <div className={Styles.fieldsContainer}>
            <div className={Styles.fieldBox}>
              <div className={Styles.fieldRow}>
                <div className={Styles.fieldItem}>
                  <Heading level={5}>Default blog for all blog posts</Heading>
                  <GoHighLevelBlogSelect
                    blogs={availableBlogs}
                    selectedBlogId={selectedBlogId}
                    onBlogSelect={setSelectedBlogId}
                    placeholder="Select a default blog"
                  />
                </div>
                <div className={Styles.fieldItem}>
                  <Heading level={5}>Default author for all blog posts</Heading>
                  <GoHighLevelAuthorSelect
                    authors={availableAuthors}
                    selectedAuthorId={selectedAuthorId}
                    onAuthorSelect={setSelectedAuthorId}
                    placeholder="Select a default author"
                  />
                </div>
              </div>
            </div>
            <Text variant="secondary" color="var(--dark-60)">
              You can change these settings anytime from the Integrations page or when editing a post.
            </Text>
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton variant="primary" onPress={handleSaveDefaults} isDisabled={!canSave || isLoading}>
            {isLoading ? 'Saving...' : 'Confirm'}
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
